import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { scrollToSection } from "../../i18n";

const EASE = [0.22, 1, 0.36, 1];

export default function Quiz({ content, onComplete }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const total = content.questions.length;
  const finished = step >= total;

  const answer = (opt) => {
    const next = [...answers, opt];
    setAnswers(next);
    if (step + 1 >= total) {
      onComplete(next);
      setStep(total);
    } else {
      setStep(step + 1);
    }
  };

  return (
    <div data-testid="services-quiz" className="rounded-[2rem] bg-white p-7 shadow-[0_30px_70px_-25px_rgba(15,43,58,0.3)] ring-1 ring-navy/10 sm:p-10">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl">{content.title}</h2>
          <p className="mt-1 text-sm font-semibold text-mute">{content.sub}</p>
        </div>
        <div className="flex gap-1.5">
          {content.questions.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${i < step ? "w-7 bg-sky" : i === step ? "w-7 bg-navy" : "w-4 bg-navy/15"}`} />
          ))}
        </div>
      </div>

      <div className="mt-8 min-h-[180px]">
        <AnimatePresence mode="wait">
          {!finished ? (
            <motion.div key={step} initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }} transition={{ duration: 0.4, ease: EASE }}>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-sky">
                {content.step} {step + 1} / {total}
              </p>
              <p data-testid={`quiz-question-${step}`} className="mt-3 font-display text-xl font-semibold text-navy sm:text-2xl">
                {content.questions[step].q}
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {content.questions[step].options.map((opt) => (
                  <button
                    key={opt}
                    data-testid={`quiz-answer-${step}-${opt.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => answer(opt)}
                    className="rounded-full bg-sand px-5 py-3 text-sm font-bold text-navy ring-1 ring-navy/10 transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-navy hover:text-white"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex flex-col items-start gap-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-sky">
                <Check className="h-6 w-6" strokeWidth={3} />
              </span>
              <p data-testid="quiz-done" className="font-display text-xl italic leading-snug text-navy sm:text-2xl">
                {content.done}
              </p>
              <button data-testid="quiz-continue" onClick={() => scrollToSection("#valutazione")} className="btn-primary">
                {content.cta}
                <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
